import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { MetricCardSkeleton } from '../../ui/Skeleton';
import { useFormatting } from '../../../hooks/useFormatting';

/**
 * Metric Card Component
 * Displays a single metric with optional trend indicator, subtitle, and sparkline
 * @param {Object} props - Component props
 * @param {string} props.title - Metric title
 * @param {string|number} props.value - Metric value to display
 * @param {boolean} [props.loading] - Loading state
 * @param {React.ReactNode} [props.icon] - Icon element
 * @param {string} [props.color] - Color variant ('blue', 'green', 'red', etc.)
 * @param {string} [props.subtitle] - Subtitle text displayed below value
 * @param {number|null} [props.trend] - Trend percentage (positive/negative)
 * @param {string} [props.trendLabel] - Trend label text
 * @param {string} [props.className] - Additional CSS classes
 * @param {string} [props.formatType] - Format type ('number' | 'currency' | null)
 * @param {boolean} [props.showIcon] - Whether to show icon
 * @param {string} [props.size] - Size variant ('small' | 'medium' | 'large')
 */
const MetricCard = ({ 
  title, 
  value, 
  loading = false, 
  icon = '', 
  color = 'blue',
  subtitle = '',
  trend = null,
  trendLabel = '',
  className = '',
  formatType = null, // 'number' | 'currency' | null
  showIcon = false,
  size = 'medium', // 'small' | 'medium' | 'large'
  onClick,
  metricId,
  detailPath,
  description = '', // Human-readable description for tooltip
  tooltip = '' // Tooltip text (falls back to description)
}) => {
  const { formatCurrency, formatNumber } = useFormatting();
  const navigate = useNavigate();

  const handleClick = () => {
    if (onClick) {
      onClick(metricId || title);
    } else if (detailPath) {
      navigate(detailPath);
    } else if (metricId) {
      navigate(`/dashboard/details/${metricId}`);
    }
  };
  
  const getTrendIcon = () => {
    if (!trend) return null;
    if (trend > 0) return <i className="bi bi-arrow-up icon-brand"></i>;
    if (trend < 0) return <i className="bi bi-arrow-down icon-brand"></i>;
    return <i className="bi bi-arrow-right icon-brand"></i>;
  };

  const getTrendClass = () => {
    if (!trend) return '';
    if (trend > 0) return 'positive';
    if (trend < 0) return 'negative';
    return 'neutral';
  };

  const isClickable = onClick || detailPath || metricId;
  
  return (
    <div 
      className={`metric-card ${color} ${size} ${className} ${isClickable ? 'metric-card-clickable' : ''}`}
      role={isClickable ? "button" : "region"}
      aria-label={`${title} metric${isClickable ? ' - Click to view details' : ''}`}
      aria-busy={loading}
      onClick={isClickable ? handleClick : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      } : undefined}
    >
      <div className="metric-header">
        {(icon || showIcon) && icon ? (
          <div className="metric-icon" aria-hidden="true">{icon}</div>
        ) : null}
        <div className="metric-title">
          {title}
          {(tooltip || description) && (
            <span 
              className="metric-tooltip-trigger" 
              title={tooltip || description}
              aria-label={`${title}: ${tooltip || description}`}
            >
              <i className="bi bi-info-circle" aria-hidden="true"></i>
            </span>
          )}
        </div>
      </div>
      
      <div className="metric-content">
        {loading ? (
          <MetricCardSkeleton />
        ) : (
          <>
            <div className="metric-value">
              {(() => {
                // Ensure value is a number for formatting
                const numValue = typeof value === 'number' ? value : Number(value);
                if (formatType === 'number' && !isNaN(numValue)) return formatNumber(numValue);
                if (formatType === 'currency' && !isNaN(numValue)) return formatCurrency(numValue);
                // Fallback: return value as-is if not a valid number or no format type
                return isNaN(numValue) ? '0' : String(value);
              })()}
              {trend !== null && trend !== undefined && (
                <span className={`metric-trend-badge ${getTrendClass()}`}>
                  {getTrendIcon()} {Math.abs(trend).toFixed(1)}%
                </span>
              )}
            </div>
            {subtitle && <div className="metric-subtitle">{subtitle}</div>}
            {/* Optional sparkline slot (series passed via children or higher-level wrapper) */}
            {Array.isArray(value?.series) && value.series.length > 1 ? (
              <Sparkline data={value.series} />
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

MetricCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  loading: PropTypes.bool,
  icon: PropTypes.node,
  color: PropTypes.string,
  subtitle: PropTypes.string,
  trend: PropTypes.number,
  trendLabel: PropTypes.string,
  className: PropTypes.string,
  formatType: PropTypes.oneOf(['number', 'currency', null]),
  showIcon: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  onClick: PropTypes.func,
  metricId: PropTypes.string,
  detailPath: PropTypes.string,
  description: PropTypes.string,
  tooltip: PropTypes.string
};

MetricCard.defaultProps = {
  loading: false,
  icon: '',
  color: 'blue',
  subtitle: '',
  trend: null,
  trendLabel: '',
  className: '',
  formatType: null,
  showIcon: false
};

export default React.memo(MetricCard);

/**
 * Sparkline Component
 * Lightweight sparkline using inline SVG; expects small arrays (<=50 points)
 * @param {Object} props - Component props
 * @param {Array<number>} [props.data] - Array of numeric values to plot
 */
const Sparkline = ({ data = [] }) => {
  const width = 160; const height = 36; const padding = 2;
  const max = Math.max(...data); const min = Math.min(...data);
  const norm = (v) => {
    if (max === min) return height / 2;
    return height - ((v - min) / (max - min)) * height;
  };
  const step = (width - padding * 2) / (data.length - 1);
  const d = data.map((v, i) => `${i === 0 ? 'M' : 'L'} ${padding + i * step} ${norm(v)}`).join(' ');
  return (
    <svg className="metric-sparkline" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
      <path d={d} fill="none" stroke="var(--admin-accent)" strokeWidth="2" />
    </svg>
  );
};

Sparkline.propTypes = {
  data: PropTypes.arrayOf(PropTypes.number)
};
